<script setup>
import { onBeforeMount, reactive, ref, computed, watch, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ModalComponent from '@/modules/shared/components/ModalComponent.vue';
import PanelLayout from '@/modules/shared/pages/PanelLayout.vue';
import Header from '@/modules/shared/components/Header.vue';
import { useMainStore } from '@/modules/shared/stores/mainStore.ts';
import useDashboard from '@/modules/dashboard/composables/useDashboard';
import useAlerts from '@/modules/explore/composables/useAlerts';
import useMailboxes from '@/modules/settings/composables/useMailboxes';
import useCompanyBusinessHours from '@/modules/settings/composables/useCompanyBusinessHours';
import useContactGroups from '@/modules/settings/composables/useContactGroups';
import useUsers from '@/modules/settings/composables/useUsers';
import useTeams from '@/modules/settings/composables/useTeams';
import useMetricAdjustments from '@/modules/settings/composables/useMetricAdjustments';
import { HomeIcon, Cog8ToothIcon, BellIcon, BookOpenIcon, QuestionMarkCircleIcon } from '@heroicons/vue/16/solid';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import useDemoApi from '@/modules/shared/composable/useDemoApi';

const router = useRouter();
const route = useRoute();
const { getMe } = useDemoApi();
const { getDashboard } = useDashboard();
const { getAlerts } = useAlerts();
const { getMailboxes } = useMailboxes();
const { getCompanyBusinessHours } = useCompanyBusinessHours();
const { getContactGroups } = useContactGroups();
const { getUsers } = useUsers();
const { getTeams } = useTeams();
const { getMetricAdjustments } = useMetricAdjustments();

const sideBar = reactive({
    headerItem: [],
    footerItem: []
});
const mainStore = useMainStore();
const isOpen = ref(false);
const user = ref();
const url = new URL(window.location.href);
const handleOpen = computed(() => isOpen.value);

const itemRight = {
    icon: QuestionMarkCircleIcon,
    typeComponent: 'button',
    text: 'Help',
    handlerClick: () => window.open('https://docs.enterprise.emailmeter.com/', '_blank')
};

user.value = getMe();

watch(
    () => route.path,
    (newPath) => {
        if (newPath) isOpen.value = false;
    }
);

onBeforeMount(async () => {
    if (url.pathname !== '/login') {
        const savedToken = localStorage.getItem('userToken') || '123';
        const urlToken = url.searchParams.get('token') || '123';

        if (savedToken.length > 0 || urlToken.length > 0) {
            localStorage.setItem('userToken', urlToken.length > 0 ? urlToken : savedToken);

            if (urlToken !== '') {
                await getDashboard();
                if (user.value.roles === 'admin') await initApp();
                if (url.pathname.includes('undefined')) router.push({ name: 'dashboard' });
            }
        } else {
            localStorage.setItem('errorLogin', url.searchParams.get('error') || '');
            router.push({ name: 'login' });
        }
    }
    updateWindowWidth();
    window.addEventListener('resize', updateWindowWidth);
});

const updateWindowWidth = () => {
    const windowWidth = window.innerWidth || document.body.clientWidth;
    mainStore.setWindowWidth(windowWidth);
};

const removeListener = () => {
    window.removeEventListener('resize', updateWindowWidth);
};

const initApp = async () => {
    getAlerts();
    getMailboxes();
    getUsers();
    getTeams();
    getCompanyBusinessHours();
    getMetricAdjustments();
    getContactGroups();
};

onBeforeUnmount(() => removeListener());

sideBar.headerItem = [
    {
        typeComponent: 'router-link',
        icon: HomeIcon,
        text: 'Dashboard',
        to: '/dashboard'
    },
    {
        typeComponent: 'router-link',
        icon: BellIcon,
        text: 'Alerts',
        to: '/alerts'
    },
    {
        icon: Cog8ToothIcon,
        typeComponent: 'router-link',
        text: 'Settings',
        to: '/settings/mailboxes'
    }
];
sideBar.footerItem = [
    {
        icon: BookOpenIcon,
        typeComponent: 'button',
        text: 'Documentation',
        handlerClick: () => window.open('https://docs.enterprise.emailmeter.com/introduction', '_blank')
    }
];
</script>

<template>
    <ModalComponent />
    <RouterView v-if="route.fullPath.includes('/login')" />
    <div v-else-if="user" :key="isOpen" class="h-dvh w-screen bg-gray-50!">
        <PanelLayout class="h-full" :close="handleOpen" :user="user" :header-items="sideBar.headerItem" :footer-items="sideBar.footerItem" @is-open="isOpen = true" />
        <div v-if="mainStore.isMobileOrTablet && isOpen" class="bg-gray-950/70 absolute top-0 bottom-0 left-[326px] right-0">
            <div class="h-10 w-10 p-2 fixed right-2 top-3 text-white cursor-pointer rounded-lg hover:bg-white/10" @click="isOpen = false">
                <XMarkIcon class="h-6 w-6" />
            </div>
        </div>
        <div
            class="bg-white flex flex-col absolute right-0 flex-1 overflow-y-auto"
            :class="[
                mainStore.isMobileOrTablet ? 'left-0 top-16 bottom-0' : 'top-11 bottom-0 left-[221px] shadow-xs rounded-tl-xl border-l-1 mt-[10px] border border-t-1 border-gray-200',
                isOpen ? 'hidden' : ''
            ]"
        >
            <Header class="min-w-[720px]" :items="sideBar.headerItem" :item-right="itemRight" />
            <RouterView class="flex flex-col mx-auto min-w-[720px] p-5" />
        </div>
    </div>
</template>
